import React, { useState, useEffect } from 'react';
import { Edit, useForm } from '@refinedev/antd';
import { useCustom } from '@refinedev/core';
import { Form, Input, Select, InputNumber, Switch, Row, Col, Card } from 'antd';
import { ImageUploadField } from '../../components/ImageUploadField';
import { LocalizedContentTabs, normalizeTranslations } from '../../components/LocalizedContentTabs';
import { API_BASE_URL } from '../../providers/dataProvider';

const LANG_FIELDS = [
  { key: 'name', label: 'Name', required: true },
  { key: 'description', label: 'Description', textarea: true, rows: 2 },
];

export const MealEdit: React.FC = () => {
  const { formProps, saveButtonProps, queryResult } = useForm({
    resource: 'meals',
    action: 'edit',
  });

  const form = Form.useFormInstance();
  const selectedRestaurantId = Form.useWatch('restaurantId', form);

  const { data: restaurantsData } = useCustom({
    url: '/restaurant',
    method: 'get',
    config: { query: { limit: 100 } },
  });
  const restaurants = restaurantsData?.data?.items || [];

  const [categories, setCategories] = useState<any[]>([]);

  useEffect(() => {
    if (selectedRestaurantId) {
      fetch(`${API_BASE_URL}/restaurant/${selectedRestaurantId}/categories`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('access_token')}` },
      })
        .then((r) => r.json())
        .then((data) => {
          setCategories(Array.isArray(data) ? data : data?.items || []);
        });
    } else {
      setCategories([]);
    }
  }, [selectedRestaurantId]);

  useEffect(() => {
    const existingRestaurantId = queryResult?.data?.data?.restaurantId;
    if (existingRestaurantId && !selectedRestaurantId) {
      fetch(`${API_BASE_URL}/restaurant/${existingRestaurantId}/categories`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('access_token')}` },
      })
        .then((r) => r.json())
        .then((data) => {
          setCategories(Array.isArray(data) ? data : data?.items || []);
        });
    }
  }, [queryResult?.data?.data?.restaurantId, selectedRestaurantId]);

  return (
    <Edit resource="meals" saveButtonProps={saveButtonProps}>
      <Form
        {...formProps}
        layout="vertical"
        onFinish={(values: any) =>
          formProps.onFinish?.({
            ...values,
            translations: normalizeTranslations(values.translations, LANG_FIELDS, queryResult?.data?.data?.translations),
          })
        }
      >
        <Card title="Image" size="small" style={{ marginBottom: 24 }}>
          <Form.Item label="Meal Photo" name="imageUrl">
            <ImageUploadField subPath="meal" previewWidth={240} previewHeight={160} />
          </Form.Item>
        </Card>

        <Card title="Basic Info" size="small" style={{ marginBottom: 24 }}>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item label="Restaurant" name="restaurantId" rules={[{ required: true }]}>
                <Select placeholder="Select a restaurant" showSearch optionFilterProp="children">
                  {restaurants.map((r: any) => (
                    <Select.Option key={r.id} value={r.id}>{r.name}</Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label="Menu Category" name="menuCategoryId">
                <Select placeholder="Select a category" allowClear disabled={!selectedRestaurantId && categories.length === 0}>
                  {categories.map((c: any) => (
                    <Select.Option key={c.id} value={c.id}>{c.name}</Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>
          <LocalizedContentTabs fields={LANG_FIELDS} showSharedDivider={false} />
          <Form.Item label="Tags" name="tags">
            <Select mode="tags" placeholder="e.g. high-protein, low-carb" />
          </Form.Item>
        </Card>

        <Card title="Nutrition per Serving" size="small" style={{ marginBottom: 24 }}>
          <Row gutter={16}>
            <Col span={6}>
              <Form.Item label="Serving Size" name="servingSize">
                <InputNumber style={{ width: '100%' }} min={0.1} step={0.1} />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item label="Serving Unit" name="servingUnit">
                <Input />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item label="Calories (kcal)" name="caloriesPerServing" rules={[{ required: true }]}>
                <InputNumber style={{ width: '100%' }} min={0} />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={6}>
              <Form.Item label="Protein (g)" name="proteinPerServing">
                <InputNumber style={{ width: '100%' }} min={0} />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item label="Fat (g)" name="fatPerServing">
                <InputNumber style={{ width: '100%' }} min={0} />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item label="Carbs (g)" name="carbsPerServing">
                <InputNumber style={{ width: '100%' }} min={0} />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item label="Fiber (g)" name="fiberPerServing">
                <InputNumber style={{ width: '100%' }} min={0} />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={6}>
              <Form.Item label="Sodium (mg)" name="sodiumPerServing">
                <InputNumber style={{ width: '100%' }} min={0} />
              </Form.Item>
            </Col>
          </Row>
        </Card>

        <Row gutter={16}>
          <Col span={8}>
            <Form.Item label="Sort Order" name="sortOrder">
              <InputNumber style={{ width: '100%' }} min={0} />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item label="Active" name="isActive" valuePropName="checked">
              <Switch />
            </Form.Item>
          </Col>
        </Row>
      </Form>
    </Edit>
  );
};
